// Module: CartScreen
// Role: Cart-step screenshot brick with article confirmation prompt.
//       Extends ScreenshotBrick pattern with a product-confirm card.
// Uses:
//   - hooks/useFileUpload:useFileUpload
//   - api/flow-client:useArtefactPresign, useSubmitCheckpoint
//   - shared/limits:MAX_PHOTO_INPUT_BYTES
//   - providers/PlatformProvider:PlatformContext
//   - lib/uploadTelemetry:createUploadFunnel, uploadTimeoutFor
//   - lib/imageResize:ImageResizeError
//   - screens/pantene/_uploadXHR:UploadTimeoutError
//   - atoms: Frame, IOSStatusBar, MiniHeader, StepStrip, InstructionCard, CtaBar, HintBar
// Used by: FlowScreen.tsx (kind='CART')
//
// FTR-1135: Creator confirms the correct product is in cart before upload.
// FTR-1317: funnel telemetry replaces ad-hoc apiFetch beacons.
// ADR-023: no platform literals.

import { useContext, useRef, useState, useCallback } from 'react';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { PlatformContext } from '../../../providers/PlatformProvider';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useArtefactPresign, useSubmitCheckpoint } from '../../../api/flow-client';
import { MAX_PHOTO_INPUT_BYTES, FLOW_PHOTO_MAX_DIM } from '../../../shared/limits';
import { resizeImageToBlob, ImageResizeError } from '../../../lib/imageResize';
import { createUploadFunnel, uploadTimeoutFor } from '../../../lib/uploadTelemetry';
import { UploadTimeoutError } from '../../../screens/pantene/_uploadXHR';
import { P, S, T } from '../../../styles/tokens';
import { flowStepLabel } from '../_labels';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CartScreenProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CartScreen({
  checkpoint,
  flowRun,
  checkpoints,
}: CartScreenProps) {
  const platform = useContext(PlatformContext);
  const canUpload = platform?.capabilities.fileUpload ?? false;

  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [article, setArticle] = useState('');

  const upload = useFileUpload();
  const presign = useArtefactPresign(flowRun.slot_id);
  const submitCheckpoint = useSubmitCheckpoint(flowRun.slot_id, checkpoint.id);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const parentCount = checkpoints.filter(
    (c) => c.parent_checkpoint_id === null,
  ).length;

  const handlePickFile = useCallback(() => {
    if (!article.trim()) {
      setHintMessage('Сначала введите артикул товара.');
      return;
    }
    if (!canUpload) {
      setHintMessage('Загрузка файлов недоступна на этой платформе.');
      return;
    }
    fileInputRef.current?.click();
  }, [article, canUpload]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset input value so re-picking the same file triggers onChange (BUG-1158 fix B)
      if (e.target) e.target.value = '';
      if (!file) return;

      // BUG-1304: 50 MB input guard
      if (file.size > MAX_PHOTO_INPUT_BYTES) {
        setHintMessage('Файл слишком большой. Максимум 50 МБ.');
        return;
      }

      setHintMessage(null);

      // FTR-1317: upload funnel telemetry accumulator
      const funnel = createUploadFunnel(flowRun.slot_id, checkpoint.slug);
      funnel.stage('file_picked', { mime: file.type, size: file.size });

      // BUG-1304: normalize to JPEG (money-path: cart screenshot)
      let uploadFile = file;
      try {
        funnel.stage('resize_start');
        const blob = await resizeImageToBlob(file, { maxDim: FLOW_PHOTO_MAX_DIM, quality: 0.85 });
        uploadFile = new File([blob], 'upload.jpg', { type: 'image/jpeg' });
        funnel.stage('resize_done', { size: blob.size });
      } catch (resizeErr) {
        if (resizeErr instanceof ImageResizeError && resizeErr.code === 'decode_timeout') {
          funnel.stage('resize_timeout');
          funnel.flush('timeout');
          setHintMessage('Загрузка идёт дольше обычного — проверь интернет и попробуй ещё раз');
          return;
        }
        funnel.stage('resize_failed', { code: resizeErr instanceof ImageResizeError ? resizeErr.code : undefined });
        console.warn('[FTR-1317] re-encode failed, uploading original:', resizeErr);
      }

      try {
        funnel.stage('presign_start');
        const { upload_url, artefact_path } = await presign.mutateAsync({
          checkpoint_slug: checkpoint.slug,
          artefact_kind: 'screenshot',
          content_md5: '',
          file_size_bytes: uploadFile.size,
        });
        funnel.stage('presign_done');

        funnel.stage('upload_start');
        await upload.start({ url: upload_url, file: uploadFile, timeoutMs: uploadTimeoutFor(uploadFile.size) });
        funnel.stage('upload_done');

        const thumbObjectUrl = URL.createObjectURL(uploadFile);
        setThumbUrl(thumbObjectUrl);

        funnel.stage('submit_start');
        await submitCheckpoint.mutateAsync({
          artefact_path,
          artefacts: [
            { kind: 'screenshot', artefact_path },
            { kind: 'text', value: article.trim() },
          ],
        });
        funnel.stage('submit_done');
        funnel.flush('success');
        setIsDone(true);
        platform?.hapticImpact('medium');
      } catch (err: unknown) {
        const isTimeout = err instanceof UploadTimeoutError || (err instanceof DOMException && err.name === 'AbortError');
        if (isTimeout) {
          funnel.stage('upload_timeout');
          funnel.flush('timeout');
          setHintMessage('Загрузка идёт дольше обычного — проверь интернет и попробуй ещё раз');
        } else {
          funnel.stage('upload_error');
          funnel.flush('failed');
          setHintMessage('Ошибка загрузки. Попробуйте ещё раз.');
        }
        setThumbUrl(null);
      }
    },
    [article, presign, upload, submitCheckpoint, checkpoint.slug, flowRun.slot_id, platform],
  );

  const ctaLabel = isDone ? 'Готово' : 'Загрузить скриншот корзины';
  const isUploading = upload.status === 'uploading';

  return (
    <Frame>
      <MiniHeader title={flowStepLabel(checkpoint.kind)} />

      <div style={{ padding: '12px 16px 0' }}>
        <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
      </div>

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <InstructionCard>
          {checkpoint.instructions_text && (
            <p style={{ font: T.title3, color: S.label, margin: '0 0 12px', lineHeight: 1.2 }}>
              {checkpoint.instructions_text}
            </p>
          )}

          {/* Product confirmation card */}
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 12,
              background: `rgba(226,114,91,0.08)`,
              border: `1px solid rgba(226,114,91,0.2)`,
              marginBottom: 12,
            }}
          >
            <p style={{ font: T.footnote, color: P.terracotta, margin: 0, fontWeight: 600 }}>
              Убедитесь, что в корзине именно наш товар
            </p>
            <p style={{ font: T.caption, color: S.secondaryLabel, margin: '4px 0 0' }}>
              Скриншот должен показывать товар из задания в корзине
            </p>
          </div>

          {checkpoint.step_context?.search_query && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 12,
                background: `rgba(226,114,91,0.08)`,
                border: `1px solid rgba(226,114,91,0.2)`,
              }}
            >
              <div style={{ font: T.caption, color: S.secondaryLabel, margin: 0 }}>Что искать:</div>
              <div style={{ font: T.subhead, color: S.label, fontWeight: 600, margin: '2px 0 0' }}>
                {checkpoint.step_context.search_query}
              </div>
            </div>
          )}

          <label style={{ display: 'block', marginTop: 12 }}>
            <div style={{ font: T.caption, color: S.secondaryLabel, marginBottom: 6 }}>
              Артикул товара
            </div>
            <input
              value={article}
              onChange={(e) => {
                setArticle(e.target.value);
                if (hintMessage) setHintMessage(null);
              }}
              placeholder="Например: 123456789"
              inputMode="text"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: 12,
                border: `1px solid ${P.line}`,
                background: P.card,
                color: S.label,
                font: T.callout,
                padding: '12px 14px',
                outline: 'none',
              }}
            />
          </label>
        </InstructionCard>

        {/* Thumbnail preview */}
        {thumbUrl && (
          <div style={{ borderRadius: 12, overflow: 'hidden', maxHeight: 200 }}>
            <img
              src={thumbUrl}
              alt="Скриншот корзины"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {hintMessage && <HintBar kind="warn">{hintMessage}</HintBar>}

      <WizardCtaBar
        label={ctaLabel}
        onClick={isDone ? () => {} : handlePickFile}
        disabled={isDone || isUploading || !article.trim()}
      />
    </Frame>
  );
}
